import React, { Component } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { Upload } from 'Utils/SvgIcons';
import service from 'Api/service';
import { saveError } from 'Api';
import { history } from 'Components/utils/router/history';
import ImportWizardHeader from './ImportWizardHeader';
import * as actions from 'Actions';
import { followImportProgress } from 'HelperFunctions/import_wizard';
import RecentTranslations, { saveRecent } from './RecentTranslations';

const ACCEPT = '.csv,.xlsx,.ods,.zip';
const ACCEPTED = /\.(csv|xlsx|ods|zip)$/i;

// Dream Import Wizard (TD1-10584): the Upload step becomes "drop your files here". Any mix of csv, xlsx, ods or a
// zip of those goes to the API as one set; it is detected and translated in the background into the wizard's own
// CSVs and the Translate step takes over. The single-file template path stays one link away.
class ImportWizardDreamUpload extends Component {
  state = {
    files: [],
    dragging: false,
    uploading: false,
    percent: 0,
    burst: 0,
    message: '',
  };

  componentDidUpdate(prevProps) {
    const permissions = prevProps.business?.permissions;
    if (
      permissions &&
      !(permissions.importWizardEnabled && permissions.dreamImportWizardEnabled)
    ) {
      history.push('/unauthorized');
    }
  }

  addFiles = (list) => {
    const incoming = Array.from(list || []);
    const rejected = incoming.filter((file) => !ACCEPTED.test(file.name));
    const accepted = incoming.filter((file) => ACCEPTED.test(file.name));
    this.setState(({ files, burst }) => ({
      burst: accepted.length ? burst + 1 : burst,
      files: [
        ...files,
        ...accepted.filter((file) => !files.some((f) => f.name === file.name)),
      ],
      message: rejected.length
        ? `Ignored: ${rejected.map((file) => file.name).join(', ')} (only csv, xlsx, ods or zip)`
        : '',
    }));
  };

  removeFile = (name) =>
    this.setState(({ files }) => ({
      files: files.filter((file) => file.name !== name),
    }));

  onDrop = (event) => {
    event.preventDefault();
    this.setState({ dragging: false });
    this.addFiles(event.dataTransfer.files);
  };

  handleNextStep = () => {
    const { files } = this.state;
    const { setErrors, startProgressBar, updateProgressBar, closeProgressBar } =
      this.props;
    if (!files.length) {
      this.setState({
        message: 'Drop at least one csv, xlsx, ods or zip file.',
      });
      return;
    }
    const data = new FormData();
    files.forEach((file) => data.append('files[]', file));
    this.setState({ uploading: true, message: '' });
    service
      .post(`${import.meta.env.VITE_API_DOMAIN}/api/dream_imports`, data)
      .then((response) => {
        const setId = response.data.set;
        saveRecent(this.props.business?.id, {
          id: setId,
          at: new Date().toISOString(),
          files: files.map((file) => file.name),
        });
        startProgressBar('import');
        return followImportProgress(setId, (percent) => {
          updateProgressBar(percent);
          this.setState({ percent });
        }).then(
          () => setId,
          () => setId
        );
      })
      .then((setId) => {
        closeProgressBar();
        history.push(`/importWizard/translate/${setId}`);
      })
      .catch((error) => {
        closeProgressBar();
        const message =
          error?.response?.data?.error ||
          error?.response?.data ||
          error?.response?.statusText ||
          'The files could not be uploaded.';
        setErrors(
          typeof message === 'string' ? message : JSON.stringify(message)
        );
        saveError(error);
        this.setState({ uploading: false });
      });
  };

  render() {
    const { files, dragging, uploading, percent, burst, message } = this.state;
    return (
      <div className='contentWrapper'>
        <ImportWizardHeader
          importStatus='upload'
          dream
          uploading={uploading}
          displayButtons={!uploading}
          onNextStep={this.handleNextStep}
          onCancel={() => history.push('/importWizard')}
          headerTitle='Drop Your Files'
        />
        <div className='ImportWizardUploadContainer'>
          <div className='importWizardUpload dreamImportUpload'>
            {uploading ? (
              <div className='dreamImportDigest' id='dream_import_digesting'>
                <p className='dreamImportDigestTitle'>
                  Reading your files and matching every column to TapGoods
                </p>
                <ul className='dreamImportFiles scanning'>
                  {files.map((file) => (
                    <li key={file.name}>{file.name}</li>
                  ))}
                </ul>
                <div className='dreamImportDigestBar'>
                  <span style={{ width: `${Math.max(4, percent)}%` }} />
                </div>
              </div>
            ) : (
              <label
                id='dream_import_drop_zone'
                className={`dreamImportDropZone ${dragging ? 'dragging' : ''}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (!dragging) this.setState({ dragging: true });
                }}
                onDragLeave={() => this.setState({ dragging: false })}
                onDrop={this.onDrop}
              >
                {/* Re-keyed on every drop so the sparkle burst replays. */}
                {burst > 0 && <span key={burst} className='dreamImportBurst' />}
                <Upload />
                <p className='dreamImportDropTitle'>
                  Drop your files here. We'll take it from here.
                </p>
                <p className='dreamImportDropHint'>
                  Exports from your old rental software, spreadsheets from any
                  system, or a zip of everything (csv, xlsx, ods). Or click to
                  choose them.
                </p>
                <input
                  type='file'
                  multiple
                  accept={ACCEPT}
                  onChange={(event) => {
                    this.addFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </label>
            )}
            {files.length > 0 && !uploading && (
              <ul className='dreamImportFiles'>
                {files.map((file) => (
                  <li key={file.name}>
                    {file.name}{' '}
                    <span className='size'>
                      ({Math.max(1, Math.round(file.size / 1024))} KB)
                    </span>
                    <a
                      href='#'
                      onClick={(event) => {
                        event.preventDefault();
                        this.removeFile(file.name);
                      }}
                    >
                      remove
                    </a>
                  </li>
                ))}
              </ul>
            )}
            {message && <p className='importWizardMessage warn'>{message}</p>}
            <Link className='example' to='/importWizard'>
              I already have a TapGoods template
            </Link>
            {!uploading && (
              <RecentTranslations businessId={this.props.business?.id} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ business: state.business.business });

export default withRouter(
  connect(mapStateToProps, actions)(ImportWizardDreamUpload)
);
